import type { GroupDto, PersonDto } from "@caloteiros/shared";
import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "../../lib/api";
import { firstName } from "../../lib/format";
import {
  useAddMember,
  useBalances,
  useLinkPerson,
  usePeople,
  useRemoveMember,
  useUnlinkPerson,
} from "../../lib/queries";
import { useToast } from "../../lib/toast";
import { IconLink, IconPlus, IconTrash } from "../Icons";
import { Avatar, ConfirmDialog, Field, Money, Sheet } from "../ui";

export function PeopleTab({ group }: { group: GroupDto }): ReactElement {
  const toast = useToast();
  const people = usePeople();
  const balances = useBalances(group.id, "pairwise");
  const addMember = useAddMember(group.id);
  const removeMember = useRemoveMember(group.id);
  const linkPerson = useLinkPerson();
  const unlinkPerson = useUnlinkPerson();

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [linkTarget, setLinkTarget] = useState<PersonDto | null>(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [removeTarget, setRemoveTarget] = useState<PersonDto | null>(null);

  const balanceOf = (personId: string): number =>
    balances.data?.balances.find((row) => row.personId === personId)?.balanceCents ?? 0;

  const memberIds = new Set(group.members.map((member) => member.person.id));
  const available = (people.data ?? []).filter((person) => !memberIds.has(person.id));

  const handleAddNew = async (): Promise<void> => {
    const value = newName.trim();
    if (!value) return;
    try {
      await addMember.mutateAsync({ displayName: value });
      setNewName("");
      setAddOpen(false);
      toast.success(`${firstName(value)} entrou no rolê.`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui adicionar.");
    }
  };

  const handleLink = async (): Promise<void> => {
    if (!linkTarget) return;
    try {
      await linkPerson.mutateAsync({ personId: linkTarget.id, email: linkEmail.trim() });
      toast.success(`${firstName(linkTarget.displayName)} agora está ligado a uma conta.`);
      setLinkTarget(null);
      setLinkEmail("");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui vincular.");
    }
  };

  return (
    <div className="stack-lg">
      <div className="card">
        {group.members.map((member) => {
          const person = member.person;
          return (
            <div key={person.id} className="balance-row">
              <Avatar name={person.displayName} color={person.avatarColor} />
              <span className="grow">
                <span className="list-item-title">
                  {person.displayName}
                  {person.isMe ? " (você)" : ""}
                </span>
                <span className="faint" style={{ display: "block" }}>
                  {person.isPlaceholder ? (
                    <span className="badge">sem conta</span>
                  ) : (
                    <span className="badge badge-brand">{person.linkedUserEmail}</span>
                  )}
                </span>
              </span>
              <Money cents={balanceOf(person.id)} signed />

              {/* Vincular/desvincular so faz sentido para quem nao e voce. */}
              {!person.isMe ? (
                <>
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={
                      person.isPlaceholder
                        ? `Vincular ${person.displayName} a uma conta`
                        : `Desvincular ${person.displayName}`
                    }
                    onClick={() => {
                      if (person.isPlaceholder) {
                        setLinkTarget(person);
                      } else {
                        void unlinkPerson
                          .mutateAsync(person.id)
                          .then(() => toast.success("Vínculo desfeito."))
                          .catch(() => toast.error("Não consegui desvincular."));
                      }
                    }}
                  >
                    <IconLink width={18} height={18} />
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={`Tirar ${person.displayName} do rolê`}
                    onClick={() => setRemoveTarget(person)}
                  >
                    <IconTrash width={18} height={18} />
                  </button>
                </>
              ) : null}
            </div>
          );
        })}
      </div>

      <button type="button" className="btn btn-ghost btn-block" onClick={() => setAddOpen(true)}>
        <IconPlus width={18} height={18} />
        Adicionar pessoa
      </button>

      <Sheet open={addOpen} onClose={() => setAddOpen(false)} title="Adicionar ao rolê">
        <div className="stack-lg">
          <Field as="div" label="Nome" hint="Só o nome. A pessoa não precisa ter conta.">
            <div className="row">
              <input
                className="input grow"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void handleAddNew();
                }}
                placeholder="Ex.: Bruna"
                maxLength={80}
                autoFocus
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void handleAddNew()}
                disabled={addMember.isPending}
              >
                Add
              </button>
            </div>
          </Field>

          {available.length > 0 ? (
            <div className="stack">
              <span className="label">Do seu caderninho</span>
              <div className="card">
                {available.map((person) => (
                  <button
                    key={person.id}
                    type="button"
                    className="list-item"
                    onClick={() => {
                      addMember.mutate(
                        { personId: person.id },
                        {
                          onSuccess: () => {
                            setAddOpen(false);
                            toast.success(`${firstName(person.displayName)} entrou no rolê.`);
                          },
                          onError: () => toast.error("Não consegui adicionar."),
                        },
                      );
                    }}
                  >
                    <Avatar name={person.displayName} color={person.avatarColor} size="sm" />
                    <span className="grow">{person.displayName}</span>
                    <IconPlus width={16} height={16} />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </Sheet>

      <Sheet
        open={linkTarget !== null}
        onClose={() => setLinkTarget(null)}
        title={`Vincular ${linkTarget ? firstName(linkTarget.displayName) : ""}`}
      >
        <div className="stack-lg">
          <p className="muted">
            Informe o e-mail da conta dessa pessoa. Tudo que já foi lançado continua igual —
            muda só quem consegue enxergar o rolê.
          </p>
          <Field label="E-mail da conta">
            <input
              className="input"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              value={linkEmail}
              onChange={(event) => setLinkEmail(event.target.value)}
              placeholder="amigo@email.com"
              autoFocus
            />
          </Field>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void handleLink()}
            disabled={linkPerson.isPending}
          >
            Vincular
          </button>
        </div>
      </Sheet>

      <ConfirmDialog
        open={removeTarget !== null}
        title="Tirar do rolê?"
        message={`${removeTarget?.displayName ?? ""} sai do grupo. Se já aparecer em alguma despesa, é preciso ajustar a despesa antes.`}
        confirmLabel="Tirar"
        destructive
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => {
          const target = removeTarget;
          setRemoveTarget(null);
          if (!target) return;
          removeMember.mutate(target.id, {
            onSuccess: () => toast.success("Removido do rolê."),
            onError: (error) =>
              toast.error(error instanceof ApiError ? error.message : "Não consegui remover."),
          });
        }}
      />
    </div>
  );
}
